import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * TrustedByBrandsSection — quiet, premium credibility block between the
 * capability marquee and Selected Work.
 *
 * Metrics animate a subtle count-up when the section enters the viewport
 * (once per page load). Only the numeric value animates; the "+" sign and
 * the red full stop stay static throughout. Width is reserved so no digit
 * change causes any horizontal shift.
 */

const METRICS = [
    { value: 30, plus: true, label: "Brands Worked With", digitReserve: 2, delayMs: 0 },
    { value: 100, plus: true, label: "Campaigns Delivered", digitReserve: 3, delayMs: 130 },
    { value: 8, plus: false, label: "Years of Craft", digitReserve: 1, delayMs: 260 },
];

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const handler = (e) => setReduced(e.matches);
        mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
        return () => {
            mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
        };
    }, []);
    return reduced;
}

/**
 * Runs a count-up animation from 0 to `target` when the containing DOM node
 * first intersects the viewport. Fires exactly once per mount.
 *
 * Reduced-motion users receive the final value immediately and never spin
 * up the rAF loop.
 */
function useCountUp({ target, durationMs = 1500, delayMs = 0, reduced }) {
    const [value, setValue] = useState(reduced ? target : 0);
    const nodeRef = useRef(null);
    const startedRef = useRef(false);

    useEffect(() => {
        if (reduced) {
            setValue(target);
            return undefined;
        }
        const node = nodeRef.current;
        if (!node) return undefined;

        // Fallback: environments without IntersectionObserver — just play now.
        if (typeof IntersectionObserver === "undefined") {
            startedRef.current = true;
            runAnimation();
            return undefined;
        }

        let rafId = null;
        let timeoutId = null;

        function runAnimation() {
            const startTs = performance.now();
            function tick(now) {
                const elapsed = now - startTs;
                const t = Math.min(1, elapsed / durationMs);
                // ease-out cubic — smooth deceleration, no bounce, no overshoot
                const eased = 1 - Math.pow(1 - t, 3);
                if (t < 1) {
                    setValue(Math.round(eased * target));
                    rafId = requestAnimationFrame(tick);
                } else {
                    setValue(target); // land on exact target
                }
            }
            rafId = requestAnimationFrame(tick);
        }

        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !startedRef.current) {
                        startedRef.current = true;
                        timeoutId = window.setTimeout(runAnimation, delayMs);
                        io.disconnect();
                    }
                }
            },
            { threshold: 0.35 }
        );
        io.observe(node);

        return () => {
            io.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
            if (timeoutId) window.clearTimeout(timeoutId);
        };
    }, [target, durationMs, delayMs, reduced]);

    return { value, nodeRef };
}

function AnimatedMetric({ metric, index, reduced }) {
    const { value, nodeRef } = useCountUp({
        target: metric.value,
        durationMs: 1500,
        delayMs: metric.delayMs,
        reduced,
    });

    const suffix = metric.plus ? "+" : "";
    const finalLabel = `${metric.value}${suffix}`;

    return (
        <motion.div
            ref={nodeRef}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.08 * index, ease: [0.16, 1, 0.3, 1] }}
            className="text-center relative px-6 md:px-4 md:not-first:before:absolute md:not-first:before:left-0 md:not-first:before:top-4 md:not-first:before:bottom-4 md:not-first:before:w-px md:not-first:before:bg-white/10"
            data-testid={`metric-${index + 1}`}
        >
            <div
                className="font-display text-5xl md:text-6xl lg:text-7xl leading-none text-white"
                style={{ fontVariantNumeric: "tabular-nums" }}
            >
                {/* Visually-hidden final value for screen readers.
                    Assistive tech announces the target label only. */}
                <span className="sr-only">{finalLabel}</span>

                {/* Animated number — hidden from screen readers so they do not
                    announce every intermediate integer. Width is reserved via
                    min-width in ch units so digit growth causes no shift. */}
                <span
                    aria-hidden="true"
                    className="inline-block text-right"
                    style={{ minWidth: `${metric.digitReserve}ch` }}
                    data-testid={`metric-${index + 1}-number`}
                >
                    {value}
                </span>
                {metric.plus ? <span aria-hidden="true">+</span> : null}
                <span className="text-[#FF2A2A]" aria-hidden="true">.</span>
            </div>
            <div className="mt-3 font-mono text-[11px] md:text-[10px] lg:text-xs uppercase tracking-[0.24em] md:tracking-[0.18em] lg:tracking-[0.28em] text-white/60 whitespace-nowrap">
                {metric.label}
            </div>
        </motion.div>
    );
}

export default function TrustedByBrandsSection() {
    const reducedMotion = usePrefersReducedMotion();

    return (
        <section
            data-testid="home-trusted-brands-section"
            aria-label="Trusted by brands"
            className="relative bg-black text-white pt-6 pb-16 md:py-20 lg:py-24 border-y border-white/5"
        >
            <div className="container-editorial">
                {/* Semantic heading — visually hidden but reachable by screen readers
                    and heading-navigation shortcuts. The decorative visual label
                    below remains the "TRUSTED BY BRANDS" hairline pill. */}
                <h2 className="sr-only">Trusted by brands</h2>

                <div className="text-center mb-10 md:mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-flex items-center gap-3"
                    >
                        <span className="h-px w-8 bg-white/25" aria-hidden="true" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/70">
                            Trusted by Brands
                        </span>
                        <span className="h-px w-8 bg-white/25" aria-hidden="true" />
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-4 text-sm md:text-base text-white/55 max-w-xl mx-auto"
                    >
                        A selection of brands our wider REFORM team has worked with.
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="trusted-brands-viewport mx-auto"
                >
                    {/* Seamless marquee \u2014 two identical tracks positioned
                        side-by-side. The whole marquee is translated left by
                        50% of its own width per animation loop, which is
                        exactly one track. The moment the loop restarts the
                        second (duplicate) track sits where the first was,
                        so there is no visible jump. Duration is tuned via
                        CSS at desktop (~30s) and mobile (~20s). Reduced
                        motion falls back to a manually scrollable strip. */}
                    <div
                        className="trusted-brands-marquee"
                        data-testid="trusted-brands-marquee"
                    >
                        {[0, 1].map((copyIndex) => (
                            <div
                                key={copyIndex}
                                className="trusted-brands-track"
                                style={{ aspectRatio: "2000 / 667" }}
                                aria-hidden={copyIndex === 1 ? "true" : undefined}
                            >
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet="/media/trusted-brands-1200.webp 1200w, /media/trusted-brands-2000.webp 2000w"
                                        sizes="(min-width: 1024px) 78vw, (min-width: 640px) 92vw, 180vw"
                                    />
                                    <img
                                        src="/media/trusted-brands-2000.png"
                                        srcSet="/media/trusted-brands-2000.png 2000w"
                                        width="2000"
                                        height="667"
                                        alt={copyIndex === 0 ? "Selected brands worked with by the REFORM team, including Mothercare, Spritzer, Sunquick, Seoul Garden, Fernleaf, ASUS, Watsons, Pixy, Mr DIY, URC Munchy's, ONEDOC, Dior, Fujifilm, Jinro, Cetaphil, Darlie and Axis-Y" : ""}
                                        loading="lazy"
                                        decoding="async"
                                        className="block w-full h-auto select-none"
                                        draggable={false}
                                        data-testid={copyIndex === 0 ? "trusted-brands-logo-strip" : undefined}
                                    />
                                </picture>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <div
                    className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 max-w-5xl mx-auto"
                    data-testid="home-achievement-metrics"
                >
                    {METRICS.map((m, i) => (
                        <AnimatedMetric key={m.label} metric={m} index={i} reduced={reducedMotion} />
                    ))}
                </div>
            </div>
        </section>
    );
}
